import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdvancedSearch from "../../components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import queryKeys from "../../configs/react-query/query-key";
import { useMutationData } from "../../hooks/useMutationData";
import { useSearchInfo } from "../../hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { convertTimeStamp } from "../../utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { AdjustIcon } from "../Icons";
import TableWithPagination from "../Tables/TableWithPagination";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import useEscapeKey from "@hooks/useEscapeKey";

const INITIAL_PREVIEW_SEARCH_CRITERIA = {
  projectPreviewName: "",
  createdAtFrom: "",
  createdAtTo: "",
  createrId: "",
};

const PreviewSelectionModal = ({ isOpen, onClose, onSelect, creatorList, selectedPreview = null, "data-testid": dataTestId = "preview-selection-modal" }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [previewList, setPreviewList] = useState([]);
  const [totalPreviewData, setTotalPreviewData] = useState(0);
  const [totalPreviewPages, setTotalPreviewPages] = useState(0);
  const [searchPreviewCriteria, setSearchPreviewCriteria] = useState(INITIAL_PREVIEW_SEARCH_CRITERIA);
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchPreviewCriteria);
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1);
  const [tempSelectedProjectPreviewSeq, setTempSelectedProjectPreviewSeq] = useState();
  const [selectedPreviewItem, setSelectedPreviewItem] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PREVIEW_SELECTION_MODAL, DEFAULT_ITEMS_PER_PAGE);
  const isInitialMount = useRef(true);

 const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const advancedSearchFields = [
    {
      name: "createrId",
      label: "Creator",
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
      placeholder: t("system.creator-placeholder"),
    },
    {
      name: "createdAtFrom",
      type: "date",
      placeholder: t("common.create-date-from"),
    },
    {
      name: "createdAtTo",
      type: "date",
      placeholder: t("common.create-date-to"),
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle, setShowAdvancedSearch } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "projectPreviewName",
    setSearchCriteria: setSearchPreviewCriteria,
    initialCriteria: INITIAL_PREVIEW_SEARCH_CRITERIA,
  });

  // Call API to get preview data
  const getPreviewListData = useMutationData(
    queryKeys.getProjectPreviews,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setPreviewList(response.object);
          setTotalPreviewData(response.objectCount);
          setTotalPreviewPages(Math.ceil(response.objectCount / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const fetchPreviewData = (data) => {
    const effectiveData = resolveSearchCriteria(data, {
      quickSearchField: "projectPreviewName",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveData).reduce((acc, key) => {
      const value = effectiveData[key];
      if (value !== "" && value !== undefined && value !== null) {
        if ((key === "createdAtFrom" || key === "createdAtTo") && value instanceof Date) {
          acc[key] = value.toISOString().split("T")[0]; // Format: YYYY-MM-DD
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPreviewPage - 1);
    getPreviewListData.mutate(bodyRequest);
  };

  // -----------------------------------------------------------------------------
  // EFFECT 1: Handle open and close modal
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      // Set flag to indicate that the modal has been mounted, so we can skip the child effects
      isInitialMount.current = true;

      // Reset all state to default
      setShowAdvancedSearch(false);
      setCurrentPreviewPage(1);
      const initialCriteria = {
        projectPreviewName: "",
        createdAtFrom: "",
        createdAtTo: "",
        createrId: "",
      };
      setSearchPreviewCriteria(initialCriteria);
      setDebouncedSearchCriteria(initialCriteria); // Sync debounced state

      // Reset selection state and initialize from selectedPreview prop
      if (selectedPreview) {
        setTempSelectedProjectPreviewSeq([selectedPreview.projectPreviewSeq]);
        setSelectedPreviewItem(selectedPreview);
      } else {
        setTempSelectedProjectPreviewSeq(undefined);
        setSelectedPreviewItem(null);
      }

      // Call API initially - only once
      // Create a clean request body from the initial criteria
      const bodyRequest = {
        pageSize: itemsPerPage,
        startRow: 0,
      };
      getPreviewListData.mutate(bodyRequest);

    } else {
      // When closing modal (cleanup)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    }
  }, [isOpen, selectedPreview]);


  // -----------------------------------------------------------------------------
  // EFFECT 2: Handle debounce when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    // Skip if modal is not open
    if (!isOpen) return;

    // Skip first run when search criteria is reset when opening modal
    if (isInitialMount.current) return;

    setIsSearching(true);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // When user types search, always reset to page 1
      if (currentPreviewPage !== 1) {
        setCurrentPreviewPage(1);
      }
      setDebouncedSearchCriteria(searchPreviewCriteria);
      setIsSearching(false);
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchPreviewCriteria]);


  // -----------------------------------------------------------------------------
  // EFFECT 3: Handle call API when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    // Skip if modal is not open
    if (!isOpen) return;

    // Skip first run when component mounts
    // isInitialMount.current will be set to false after the first run
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Call API for subsequent interactions
    fetchPreviewData(debouncedSearchCriteria);

  }, [debouncedSearchCriteria, currentPreviewPage, itemsPerPage, showAdvancedSearch]);

  // Remove unused states that might conflict with TableWithPagination

  const previewTableColumns = ["previewName", "creator", "creationDate"];
  const previewTableColumnConfig = {
    previewName: {
      style: {
        width: "35%",
        whiteSpace: "normal",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.project-preview-name"),
      customRender: (value, row) => <div>{row.projectPreviewName}</div>,
    },
    creator: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.clarity.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    creationDate: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.clarity.creation-date"),
      customRender: (value, row) => <div>{row.createdAt ? convertTimeStamp(row.createdAt) : ""}</div>,
    },
  };

  const handleCancel = () => {
    setTempSelectedProjectPreviewSeq(undefined);
    setSelectedPreviewItem(null);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };

  // Handler for Advanced Search
  const handleAdvancedSearch = (searchData) => {
    setSearchPreviewCriteria((prev) => ({
      ...prev,
      ...searchData,
    }));
  };

  const handleSelect = () => {
    onSelect(selectedPreviewItem);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };

  const handleOnRowPreviewListClick = (data) => {
    setTempSelectedProjectPreviewSeq([data.projectPreviewSeq]);
    setSelectedPreviewItem(data);
  };

  const handleOnCheckRadioChange = (data) => {
    setTempSelectedProjectPreviewSeq(data);
    const selectedItem = previewList.find((item) => item.projectPreviewSeq === data[0]);
    if (selectedItem) {
      setSelectedPreviewItem(selectedItem);
    }
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div className={`${colors.bgModal} max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("project.select-preview-title")}</h2>
          </div>
        </div>

        {/* Search Section */}
        <div className="space-y-4 px-6 pb-4 flex-shrink-0">
          {/* Filter Inputs */}
          <div className="grid grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon
                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
              />
              <input
                type="text"
                placeholder={t("project.project-preview.search-preview-name")}
                value={searchPreviewCriteria.projectPreviewName}
                onChange={(e) => setSearchPreviewCriteria((prev) => ({ ...prev, projectPreviewName: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("common.advanced-search")}</span>
              <span className="sm:hidden">Advanced</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 flex-shrink-0">
            <div
              className={`absolute -top-2 z-10 h-0 w-0`}
              style={{
                right: "calc(33.333% / 2 - 16px)",
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid #374151`,
                filter: `drop-shadow(0 0 0 1px #292D33)`,
              }}
            ></div>

            <AdvancedSearch
              title={t("common.advanced-search")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
            />
          </div>
        )}

        {/* Table Section - Scrollable */}
        <div className="flex flex-1 flex-col px-6 min-h-0 overflow-y-hidden">
          <TableWithPagination
            columns={previewTableColumns}
            columnConfig={previewTableColumnConfig}
            data={previewList}
            currentPage={currentPreviewPage}
            setCurrentPage={setCurrentPreviewPage}
            loading={getPreviewListData.isPending || isSearching}
            rowKey="projectPreviewSeq"
            totalPages={totalPreviewPages}
            totalData={totalPreviewData}
            maxRowsPerPage={itemsPerPage}
            hasRadioButton={true}
            selectedRows={tempSelectedProjectPreviewSeq}
            onCheckboxChange={handleOnCheckRadioChange}
            onRowClick={handleOnRowPreviewListClick}
            onItemsPerPageChange={handleItemsPerPageChangeWithReset}
            showPagination={true}
            scrollableBody={true}
            showItemsPerPageSelector={true}
          />
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSelect} disabled={!selectedPreviewItem}>
                {t("common.select")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewSelectionModal;
